import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ModalWrapper } from "@msuite/ui/modal";
import { canHandOver } from "@msuite/utils/incoming-invoices";
import type { IncomingInvoiceDetail } from "@msuite/utils/types";
import React from "react";
import { useAuth } from "@/hooks/use-auth";
import { HandOverApprovalModal } from "@/modals/incoming-invoices/hand-over-approval-modal";

/** Props Interface */
interface HandOverApprovalButtonProps {
	incomingInvoice: IncomingInvoiceDetail | undefined;
}

/**
 * Hand the invoice to another member. Offered while its approvals can still change, to a default
 * approver of the invoice and to the member it was handed to last — the front of the chain — and
 * to nobody while it carries a sole approval: `canHandOver`, the rule the server refuses by. A
 * sole handover is a default approver's alone.
 */
export function HandOverApprovalButton({ incomingInvoice }: HandOverApprovalButtonProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { user } = useAuth();
	const modal = useDisclosure();

	/** Computed */
	const approvals = (incomingInvoice?.approvals ?? []).map((approval) => ({
		...approval,
		userId: approval.user.id,
	}));
	const userId = user?.id ?? "";
	const isOpen =
		!!incomingInvoice && !["paid", "duplicate", "merged"].includes(incomingInvoice.state);
	const mayHandOver = isOpen && !!userId && canHandOver(approvals, userId, false);
	const mayHandOverSole = !!userId && canHandOver(approvals, userId, true);

	/** Guard */
	if (!mayHandOver || !incomingInvoice) return null;

	/** Render */
	return (
		<React.Fragment>
			<Button
				variant="primary"
				icon={<Icon name="UserPlus" />}
				onClick={modal.onOpen}
				data-cy="hand-over-incoming-invoice-approval-button"
			>
				{t("incoming_invoices.handover.hand_over")}
			</Button>
			<ModalWrapper>
				{modal.isOpen && (
					<HandOverApprovalModal
						incomingInvoice={incomingInvoice}
						canHandOverSole={mayHandOverSole}
						isOpen={modal.isOpen}
						onClose={modal.onClose}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
